import React, { useState, useEffect } from 'react';
import { Button, Icon, ListItemIcon, ListItemText, Popover, MenuItem, Typography, Divider, Fade } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import * as authActions from 'app/auth/store/actions';
import * as clusterActions from 'app/main/cluster/store/actions';
import * as credentialsActions from 'app/main/credentials/store/actions';
import * as jobActions from 'app/main/job-manager/store/actions';
import * as filesActions from 'app/main/file-manager/store/actions';
import * as settingsActions from 'app/main/settings/store/actions';
import * as providerActions from 'app/store/actions/providers';
import * as ActionsDialog from 'app/store/actions';
import UserCapacityInfo from 'app/fuse-layouts/shared-components/UserCapacityInfo';

import { red } from '@material-ui/core/colors';
import TrainingLogoutDialog from 'app/main/trainings/components/TrainingLogoutDialog';
import { determineUserRole, formatRoleLabel } from 'app/main/util/userUtils';
import { USER_ROLE, isParticipant } from 'app/main/util/enums';

const UserMenu = props => {
	const socket = props.socket;
	const dispatch = useDispatch();
	const location = useLocation();

	const user = useSelector(({ auth }) => auth.user);
	const themeMode = useSelector(({ modernDashboard }) => modernDashboard?.userPreferences?.themeMode || 'dark');

	//for to render when change connection
	useSelector(({ providers }) => providers.providers);

	const [userMenu, setUserMenu] = useState(null);
	const [timeLeft, setTimeLeft] = useState('');
	
	// Apply dark text colors when user preference is dark mode
	const isDarkMode = themeMode === 'dark';

	useEffect(() => {
		if (user.endTime) {
			const timer = setInterval(() => {
				const end = new Date(user.endTime).getTime();
				const now = new Date().getTime();
				const distance = end - now;

				if (distance < 0) {
					clearInterval(timer);
					setTimeLeft('Session Expired');
					dispatch(authActions.logoutUser());
					dispatch(ActionsDialog.openDialog({
						children: (<TrainingLogoutDialog onClose={() => dispatch(ActionsDialog.closeDialog())} />),
						maxWidth: false,
						fullWidth: false,
						TransitionComponent: Fade,
						transitionDuration: 400,
						PaperProps: { style: { backgroundColor: 'transparent', boxShadow: 'none' } },
						BackdropProps: { style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' } }
					}));
					return;
				}

				const days = Math.floor(distance / (1000 * 60 * 60 * 24));
				const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
				const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
				const seconds = Math.floor((distance % (1000 * 60)) / 1000);
				// Scale the label to the horizon: multi-day sessions (e.g. a
				// 30-day POC) read as days, not hundreds of hours; sub-day
				// keeps the h/m/s precision short workshop sessions rely on.
				if (days >= 1) {
					setTimeLeft(`${days}d ${hours}h ${minutes}m`);
				} else if (hours >= 1) {
					setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
				} else {
					setTimeLeft(`${minutes}m ${seconds}s`);
				}
			}, 1000);

			return () => clearInterval(timer);
		}
	}, [user.endTime]);

	const userMenuClick = event => setUserMenu(event.currentTarget);

	const userMenuClose = () => setUserMenu(null);

	const logout = () => {
		socket.disconnect(1);
		socket.close();

		dispatch(authActions.logoutUser());
		userMenuClose();
		dispatch(clusterActions.clearClusterReducer());
		dispatch(credentialsActions.clearCredentialsReducer());
		dispatch(jobActions.clearJobsReducer());
		dispatch(filesActions.clearFilesReducer());
		dispatch(settingsActions.clearSettingsReducer());
		dispatch(providerActions.clearProviderReducer());
	};

	return (
		<React.Fragment>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
				
				{socket.disconnected && (
					<Icon className="m-12" style={{ color: red[500], marginRight: '60px', position: 'relative' }}>
						cloud_off
					</Icon>
				)}
				{user.endTime && (
					<Typography className="mr-12" style={{ color: timeLeft === 'Session Expired' ? red[500] : 'inherit' }}>
						{timeLeft}
					</Typography>
					)}
				
				{user.role.includes('admin') && !user.organizationId && (
					<UserCapacityInfo />
				)}
							
				<Button className="h-64" onClick={userMenuClick}>
					<div className="hidden md:flex flex-col ml-12 items-start">
						<Typography 
							component="span" 
							className="normal-case font-600 flex"
							style={{ color: isDarkMode ? '#f8fafc' : 'inherit' }}
						>
							{isParticipant(user) ? `Session ${user.sessionCode}` : `${user.firstName} ${user.lastName}`}
						</Typography>
						<Typography
							className={`text-11 ${!isParticipant(user) ? 'capitalize' : 'lowercase'}`}
							style={{
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								maxWidth: '150px',
								color: isDarkMode ? '#94a3b8' : 'inherit'
							}}
						>
							{isParticipant(user) ? user.participantEmail : formatRoleLabel(determineUserRole(user.role))}
						</Typography>
					</div>

					<Icon 
						className="text-16 ml-12 hidden sm:flex" 
						variant="action"
						style={{ color: isDarkMode ? '#f8fafc' : 'inherit' }}
					>
						keyboard_arrow_down
					</Icon>
				</Button>
			</div>

			<Popover
				open={Boolean(userMenu)}
				anchorEl={userMenu}
				onClose={userMenuClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				classes={{ paper: 'py-8' }}
			>
				<MenuItem
					component={Link}
					to={user.role.includes(USER_ROLE.ADMIN) ? '/settings' : '/settings_user'}
					onClick={userMenuClose}
					disabled={user.role.includes(USER_ROLE.PARTICIPANT)}
				>
					<ListItemIcon className="min-w-40">
						<Icon>settings</Icon>
					</ListItemIcon>
					<ListItemText className="pl-0" primary="Settings" />
				</MenuItem>
				{user.authType === 'portal-sso' && (
					<MenuItem
						component="a"
						href="https://heidiai.io/dashboard"
						target="_blank"
						rel="noopener noreferrer"
						onClick={userMenuClose}
					>
						<ListItemIcon className="min-w-40">
							<Icon>account_circle</Icon>
						</ListItemIcon>
						<ListItemText className="pl-0" primary="My Account" />
					</MenuItem>
				)}
				<MenuItem
					className="w-600"
					onClick={() => {
						dispatch(isParticipant(user) ? authActions.participantDownloadSSHKey() : authActions.downloadUserSSHKey(user.username));
						userMenuClose();
					}}
				>
					<ListItemIcon className="min-w-40">
						<Icon>save_alt</Icon>
					</ListItemIcon>
					<ListItemText className="pl-0" primary="User SSH Key" />
				</MenuItem>
				<MenuItem className="w-600" onClick={() => logout()}>
					<ListItemIcon className="min-w-40">
						<Icon>exit_to_app</Icon>
					</ListItemIcon>
					<ListItemText className="pl-0" primary="Logout" />
				</MenuItem>
				<Divider variant="middle" className="my-5" />
				<div className="w-full text-center">{`v${process.env.REACT_APP_VERSION}`}</div>
			</Popover>
		</React.Fragment>
	);
};

export default UserMenu;
